// AppLock LockMenu — header dropdown: Lock Now / Change Password / Remove Password.

import { useState, useRef, useEffect } from 'react';
import { Lock, KeyRound, Trash2, ChevronDown } from 'lucide-react';
import type { AppLockInstance } from '@ascend/applock';

interface LockMenuProps {
  lock: AppLockInstance;
}

export function LockMenu({ lock }: LockMenuProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'closed' | 'change' | 'remove'>('closed');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (next.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    setBusy(true);
    try {
      await lock.changePassword(current, next);
      setMode('closed');
      setCurrent('');
      setNext('');
    } catch {
      setError('Current password is incorrect.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemovePassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await lock.removePassword(current);
      setMode('closed');
      setCurrent('');
    } catch {
      setError('Current password is incorrect.');
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setMode('closed');
    setCurrent('');
    setNext('');
    setError(null);
    setOpen(false);
  }

  if (mode === 'change') {
    return (
      <div ref={menuRef} className="panel" style={{ padding: 'var(--space-4)' }}>
        <form onSubmit={handleChangePassword} className="flex flex-col gap-3">
          <h3 className="font-semibold text-sm">Change Password</h3>
          <input
            type="password"
            className="field-input"
            placeholder="Current password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            autoFocus
          />
          <input
            type="password"
            className="field-input"
            placeholder="New password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
          />
          {error && (
            <p className="text-xs" style={{ color: 'var(--color-danger)' }} role="alert">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={busy || !current || !next}>
              {busy ? 'Changing…' : 'Change'}
            </button>
            <button type="button" className="btn" onClick={reset} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (mode === 'remove') {
    return (
      <div ref={menuRef} className="panel" style={{ padding: 'var(--space-4)' }}>
        <form onSubmit={handleRemovePassword} className="flex flex-col gap-3">
          <h3 className="font-semibold text-sm">Remove Password</h3>
          <p className="text-xs" style={{ color: 'var(--color-warn)' }}>
            This removes encryption and stores all data unencrypted. Anyone with device access can read it.
          </p>
          <input
            type="password"
            className="field-input"
            placeholder="Current password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            autoFocus
          />
          {error && (
            <p className="text-xs" style={{ color: 'var(--color-danger)' }} role="alert">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <button type="submit" className="btn" disabled={busy || !current}
              style={{ borderColor: 'var(--color-danger)', color: 'var(--color-danger)' }}
            >
              {busy ? 'Removing…' : 'Remove'}
            </button>
            <button type="button" className="btn" onClick={reset} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        className="btn"
        onClick={() => setOpen((o) => !o)}
        aria-label="App lock menu"
        aria-expanded={open}
        data-testid="applock-menu-btn"
        style={{ padding: 'var(--space-2)' }}
      >
        <Lock size={18} aria-hidden="true" style={{ color: 'var(--color-success)' }} />
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open && (
        <div
          className="panel"
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: 'var(--space-1)',
            zIndex: 50,
            minWidth: '200px',
            padding: 'var(--space-2)',
          }}
        >
          <button
            type="button"
            className="btn"
            onClick={() => {
              lock.lock('manual');
              setOpen(false);
            }}
            style={{ width: '100%', justifyContent: 'flex-start' }}
          >
            <Lock size={16} aria-hidden="true" />
            Lock Now
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => {
              setMode('change');
              setOpen(false);
            }}
            style={{ width: '100%', justifyContent: 'flex-start', marginTop: 'var(--space-1)' }}
          >
            <KeyRound size={16} aria-hidden="true" />
            Change Password
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => {
              setMode('remove');
              setOpen(false);
            }}
            style={{
              width: '100%',
              justifyContent: 'flex-start',
              marginTop: 'var(--space-1)',
              color: 'var(--color-danger)',
            }}
          >
            <Trash2 size={16} aria-hidden="true" />
            Remove Password
          </button>
        </div>
      )}
    </div>
  );
}
